<template>
  <span>
    <FeathersAutocomplete
      v-model="computedValue"
      label="Node"
      :service-name="serviceName"
      :item-text="itemText"
      :limit="100"
      :params="params"
      return-object
      @change="$emit('change')"
      @loadMore="loadMoreSearch"
      @serviceError="displaySnackbarError"
    >
      <template #append-outer>
        <v-dialog v-model="searchDialog" max-width="600">
          <template #activator="{ on: dialogOn }">
            <v-tooltip top>
              <template #activator="{ on: tooltipOn }">
                <v-btn icon v-on="{ ...tooltipOn, ...dialogOn }"
                  ><v-icon>mdi-magnify</v-icon></v-btn
                >
              </template>
              <span>Search node</span>
            </v-tooltip>
          </template>
          <v-card>
            <v-card-title> Search a node </v-card-title>
            <v-card-text>
              <v-text-field
                :dense="dense"
                v-model="nodeNameSearch"
                label="Node Name"
                :disabled="disableNameSearch"
                hint="Case sensitive"
              ></v-text-field>
              <v-text-field
                :dense="dense"
                v-model="nodeIdSearch"
                label="Node ID"
                :disabled="disableIdSearch"
              ></v-text-field>
              <v-btn
                outlined
                block
                rounded
                color="primary"
                @click="searchNode"
                :loading="dataLoading"
              >
                <v-icon>mdi-magnify</v-icon> search
              </v-btn>
              <template v-if="result.length">
                <v-divider class="my-2"></v-divider>
                <v-data-table
                  v-model="resultSelected"
                  :items="result"
                  :headers="resultTableHeaders"
                  :item-key="itemKey"
                  dense
                  hide-default-footer
                  single-select
                  @click:row="rowClick"
                >
                </v-data-table>
              </template>
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn
                outlined
                rounded
                color="primary"
                :disabled="!resultSelected.length"
                @click="setNode"
              >
                open
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </template>
    </FeathersAutocomplete>
    <v-snackbar v-model="snackbar" app right :color="snackbarInfo.color" timeout="8000">
      {{ snackbarInfo.message }}

      <v-btn color="white" dark icon @click="snackbar = false" small>
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-snackbar>
  </span>
</template>

<script>
import { mapActions } from "vuex";
import marketSettings from "@/mixins/marketsSettings";

export default {
  name: "InputMarketNode",
  components: {
    FeathersAutocomplete: () => import("@/components/FeathersAutocomplete.vue"),
  },
  mixins: [],
  props: {
    dense: {
      type: Boolean,
      required: false,
      default: false,
    },
    value: { type: Object, required: false, default: () => ({}) },
    market: {
      type: String,
      required: false,
      default: "NYISO",
      validator: (v) =>
        ["CAISO", "CHILE", "ERCOT", "ISONE", "MISO", "NYISO", "PJM", "SPP", "EIA"].includes(v),
    },
    indexType: {
      type: String,
      required: false,
      default: "",
      validator: (v) => ["Day-Ahead", "Real-Time"].includes(v),
    },
    params: {
      type: Object,
      required: false,
      default: () => ({}),
    },
  },
  data() {
    return {
      result: [],
      resultSelected: [],
      searchDialog: false,
      nodeNameSearch: "",
      nodeIdSearch: "",
      snackbar: false,
      snackbarInfo: {},
    };
  },
  computed: {
    computedValue: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit("input", v);
      },
    },
    computedIndexType() {
      if (this.indexType === "RT") {
        return "Real-Time";
      }
      if (this.indexType === "DA") {
        return "Day-Ahead";
      }
      return this.indexType;
    },
    marketSettings() {
      if (this.market === "ERCOT") {
        return marketSettings[this.market][this.computedIndexType];
      }
      return marketSettings[this.market];
    },
    disableNameSearch() {
      return ["CAISO", "CHILE", "ERCOT", "ISONE", "MISO"].includes(this.market);
    },
    disableIdSearch() {
      return ["SPP"].includes(this.market);
    },
    serviceName() {
      return this.marketSettings.nodeService;
    },
    itemText() {
      return this.marketSettings.nodeName;
    },
    itemKey() {
      return this.marketSettings.nodeKey;
    },
    dataLoading() {
      return this.$store.state[this.serviceName].isFindPending;
    },
    resultTableHeaders() {
      return [
        { text: "Name", value: this.itemText },
        { text: "ID", value: this.itemKey },
      ];
    },
  },
  watch: {
    searchDialog(v) {
      if (v) {
        this.nodeNameSearch = "";
        this.nodeIdSearch = "";
        this.result = [];
      }
    },
  },
  beforeCreate() {},
  created() {},
  beforeMount() {},
  mounted() {},
  beforeUpdate() {},
  updated() {},
  activated() {},
  deactivated() {},
  beforeDestroy() {},
  destroyed() {},
  methods: {
    ...mapActions({
      findNodes(dispatch, payload, path) {
        return dispatch(`${path}/find`, payload);
      },
    }),
    rowClick(_event, data) {
      data.select(!data.isSelected);
    },
    searchNode({ withSnackbar = false } = {}) {
      this.findNodes(
        {
          query: {
            ...(this.nodeNameSearch.length
              ? { [this.itemText]: { $like: `%${this.nodeNameSearch}%` } }
              : {}),
            ...(this.nodeIdSearch.length
              ? {
                  [this.itemKey]:
                    this.marketSettings.nodeKeyType === "number"
                      ? this.nodeIdSearch
                      : { $like: `%${this.nodeIdSearch}%` },
                }
              : {}),
          },
        },
        this.serviceName
      ).then((r) => {
        this.result = r.data;
        if (withSnackbar && !r.data.length) {
          this.snackbarInfo = {
            color: "info",
            message: `No results for '${
              this.nodeNameSearch || this.nodeIdSearch
            }'. Tip: search is case sensitive`,
          };
          this.snackbar = true;
        }
      });
    },
    loadMoreSearch($e) {
      if (!this.disableNameSearch) {
        this.nodeNameSearch = $e;
      }
      if (!this.disableIdSearch) {
        this.nodeIdSearch = $e;
      }
      this.searchNode({ withSnackbar: true });
    },
    setNode() {
      [this.computedValue] = this.resultSelected;
      this.searchDialog = false;
    },
    displaySnackbarError() {
      this.snackbarInfo = {
        color: "error",
        message: "Oops! Something went wrong while loading nodes. Please try again.",
      };
      this.snackbar = true;
    },
  },
};
</script>

<style></style>
